import { Button, FormControl, Grid, InputLabel, MenuItem, Select } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { ChangeEvent, useState } from 'react';

import {
  getAllIABCategories,
  IAB_CATEGORIES,
  IABCategoryCodeToName,
} from '~/src/dash/utils/iab-category';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import { ButtonView } from '~/src/dash/views/button-view';
import { ct, Text } from '~/src/dash/publicaUI';

interface IProps {
  // categories that appear in the table
  selectedIABCategories: string[];
  // enables/disables save button as the iab blocklist updates
  updatingIABCategories: boolean;
  hasWriteRights: boolean;
  // saves changes to iab categories
  handleChangeIABCategories: (selectedIABCategories: string[]) => void;
  // sets snackbar errors in case of iab category conflicts
  handleSetSnackbar: (message: string) => void;
  // saves iab blocklist via query in parent component
  handleSaveIABBlocklist?: (IABCatCommaSeparated: string, IABCatPrefix: string) => void;
}

export const IABBlocklist = (props: IProps) => {
  // props
  const {
    selectedIABCategories,
    handleChangeIABCategories,
    handleSetSnackbar,
    handleSaveIABBlocklist,
    hasWriteRights,
    updatingIABCategories,
  } = props;
  // state
  const [IABCatSuffix, setIABCatSuffix] = useState('');
  const [IABCatPrefix, setIABCatPrefix] = useState('');
  const [IABCatCommaSeparated, setIABCatCommaSeparated] = useState('');
  const sortedSelectedIABCategories = IABSort(selectedIABCategories);

  // adds IAB Cat from comma separated values or from dropdowns to selected
  const addIABCat = () => {
    if (!hasWriteRights) {
      return;
    }
    // if adding directly as comma separated values
    if (IABCatCommaSeparated !== '') {
      let newCats = IABCatCommaSeparated.replace(/\s*/gi, '').split(',');

      // only add non-blank, non-duplicates
      newCats = newCats.filter((v: string) => {
        return v !== '' && !selectedIABCategories.includes(v);
      });

      handleChangeIABCategories([...selectedIABCategories, ...newCats]);
      setIABCatCommaSeparated('');
      setIABCatPrefix('');
      setIABCatSuffix('');
      return;
    }

    // if adding by manual prefix or dropdown
    if (IABCatPrefix !== '') {
      let newCat = IABCatPrefix;

      if (IABCatSuffix !== '' && IABCatSuffix !== '-') {
        newCat += '-' + IABCatSuffix;
      }

      // don't add duplicates
      if (!selectedIABCategories.includes(newCat)) {
        handleChangeIABCategories([...selectedIABCategories, newCat]);
        setIABCatCommaSeparated('');
        setIABCatPrefix('');
        setIABCatSuffix('');
      } else {
        handleSetSnackbar(ct('ErrorFailedToAddItemToIABCatBlocklist', { item: newCat }));
      }
    }
  };

  // deletes iab category from selected
  const deleteIABCat = (index: number) => {
    if (!hasWriteRights) {
      return;
    }
    const newSortedSelectedIABCategories = [...sortedSelectedIABCategories];
    newSortedSelectedIABCategories.splice(index, 1);
    handleChangeIABCategories(newSortedSelectedIABCategories);
  };

  const renderIABCategoryRows = () => {
    if (sortedSelectedIABCategories.length > 0) {
      return (
        <TableBody>
          {sortedSelectedIABCategories.map((row, i) => {
            return (
              <TableRow key={row}>
                <TableCell align='left'>{row}</TableCell>
                <TableCell align='left'>{IABCategoryCodeToName(row)}</TableCell>
                <TableCell align='center'>
                  <Button
                    disabled={!hasWriteRights}
                    style={{
                      color: deleteColor,
                    }}
                    onClick={() => deleteIABCat(i)}
                  >
                    <DeleteIcon />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      );
    } else {
      return (
        <TableBody>
          <TableRow key='empty-row'>
            <TableCell align='left'></TableCell>
            <TableCell align='left'>
              <Text tKey='SelectAndAddIABCat' />
            </TableCell>
            <TableCell align='center'></TableCell>
          </TableRow>
        </TableBody>
      );
    }
  };

  return (
    <Grid container direction='column' justify='flex-start' alignItems='flex-start' spacing={1}>
      {/* IAB Category Table */}
      <Grid item>
        <Table size='small' style={{ width: '500px' }}>
          <TableHead>
            <TableRow>
              <TableCell align='left'>
                <Text tKey='IABCode' />
              </TableCell>
              <TableCell align='left'>
                <Text tKey='Description' />
              </TableCell>
              <TableCell align='center'>
                <Text tKey='Action' />
              </TableCell>
            </TableRow>
          </TableHead>
          {renderIABCategoryRows()}
        </Table>
      </Grid>
      {/* IAB dropdowns */}
      <Grid item>
        <Grid container direction='row' justify='flex-start' alignItems='flex-end' spacing={5}>
          <Grid item>
            <FormControl>
              <InputLabel>{ct('SelectItem', { item: ct('IABPrefix') })}</InputLabel>
              <Select
                name='IABCatPrefix'
                style={{
                  width: '300px',
                }}
                value={IABCatPrefix}
                onChange={(ev: ChangeEvent<{ name: string; value: string }>) =>
                  setIABCatPrefix(ev.target.value)
                }
              >
                {getAllIABCategories().map((val) => {
                  return (
                    <MenuItem value={val}>
                      {val}: {IAB_CATEGORIES[val].label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            {IABCatPrefix !== '' && (
              <FormControl>
                <InputLabel>{ct('SelectItem', { item: ct('IABSuffix') })}</InputLabel>
                <Select
                  name='IABCatSuffix'
                  style={{
                    width: '300px',
                  }}
                  value={IABCatSuffix}
                  onChange={(ev: ChangeEvent<{ name: string; value: string }>) =>
                    setIABCatSuffix(ev.target.value)
                  }
                >
                  {Object.keys((IAB_CATEGORIES[IABCatPrefix] || { values: [] }).values || {}).map(
                    (val) => {
                      return (
                        <MenuItem value={val}>
                          {val}: {IAB_CATEGORIES[IABCatPrefix].values[val]}
                        </MenuItem>
                      );
                    },
                  )}
                </Select>
              </FormControl>
            )}
          </Grid>
          {/* Add and Save Buttons */}
          <Grid item>
            <Grid
              container
              direction='row'
              justify='flex-start'
              alignItems='flex-start'
              spacing={5}
            >
              <Grid item>
                <ButtonView
                  onClick={addIABCat}
                  text={ct('AddItem', { item: ct('IABCat') })}
                  active={false}
                  disabled={!hasWriteRights}
                />
              </Grid>
              {handleSaveIABBlocklist && (
                <Grid item>
                  <ButtonView
                    onClick={() => handleSaveIABBlocklist(IABCatCommaSeparated, IABCatPrefix)}
                    text={ct('Save')}
                    active={updatingIABCategories}
                    disabled={!hasWriteRights}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

// sorts IAB categories by name and number
const IABSort = (selectedIABCategories: string[]): string[] => {
  selectedIABCategories.sort((a: string, b: string) => {
    const partsA = a.split('-');
    const partsB = b.split('-');
    const firstNumA = parseInt(partsA[0].slice(3), 10);
    const firstNumB = parseInt(partsB[0].slice(3), 10);
    if (firstNumA > firstNumB) {
      return 1;
    } else if (firstNumB > firstNumA) {
      return -1;
    } else {
      if (partsA[1] && !partsB[1]) {
        return 1;
      } else if (!partsA[1] && partsB[1]) {
        return -1;
      } else if (!partsA[1] && !partsB[1]) {
        return 0;
      } else {
        const secondNumA = parseInt(partsA[1], 10);
        const secondNumB = parseInt(partsB[1], 10);
        if (secondNumA > secondNumB) {
          return 1;
        } else if (secondNumB > secondNumA) {
          return -1;
        } else {
          return 0;
        }
      }
    }
  });
  return selectedIABCategories;
};
